/**
 * S-52 colour palettes — day, dusk, night.
 *
 * Values are taken from the IHO S-52 Annex A presentation library colour tables
 * (Appendix 2). We're using the core tokens we actually render with in phase 1.
 * Full conditional symbology is phase 3.
 *
 * The token names match S-52 conventions (NODTA, LANDA, DEPDW, …) so a designer who
 * knows the standard can drop in changes without us having to translate.
 *
 * See docs/ardent_chart_plotter_brief.md §5 and docs/technical_spec.md §4.
 */
import type { Palette } from '@/store';

export interface S52Palette {
  /** No data / outside-of-chart background. */
  NODTA: string;
  /** Land area fill. */
  LANDA: string;
  /** Land coastline. */
  CSTLN: string;
  /** Deep water — usually the page colour. */
  DEPDW: string;
  /** Medium-shallow water. */
  DEPMS: string;
  /** Shallow water. */
  DEPVS: string;
  /** Inter-tidal / drying area. */
  DEPIT: string;
  /** Depth contour line. */
  DEPCN: string;
  /** Soundings (safe — deeper than safety contour). */
  SNDG2: string;
  /** Soundings (dangerous — shallower than safety contour). */
  SNDG1: string;
  /** Buoyage — port / red. */
  CHBLK_RED: string;
  /** Buoyage — starboard / green. */
  CHBLK_GREEN: string;
  /** Buoyage — yellow (cardinals, special). */
  CHBLK_YELLOW: string;
  /** Generic chart symbol black/foreground. */
  CHBLK: string;
  /** Light yellow flare. */
  LITYW: string;
  /** Caution / wreck magenta. */
  CHMGD: string;
  /** Text on chart. */
  CHGRD: string;
  /** UI / panel background (not part of S-52 — used for app chrome). */
  UI_BG: string;
  /** UI foreground text. */
  UI_FG: string;
  /** UI accent. */
  UI_ACCENT: string;
}

export const PALETTES: Record<Palette, S52Palette> = {
  day: {
    NODTA: '#9DB7C2',
    LANDA: '#D9C29C',
    CSTLN: '#6A553B',
    DEPDW: '#FFFFFF',
    DEPMS: '#D6EAF8',
    DEPVS: '#A9CCE3',
    DEPIT: '#C2B280',
    DEPCN: '#7A8C99',
    SNDG2: '#5B6B73',
    SNDG1: '#1A1A1A',
    CHBLK_RED: '#C0392B',
    CHBLK_GREEN: '#1E8449',
    CHBLK_YELLOW: '#E1B12C',
    CHBLK: '#1A1A1A',
    LITYW: '#F1C40F',
    CHMGD: '#B03A8A',
    CHGRD: '#3A3A3A',
    UI_BG: '#F5F1E6',
    UI_FG: '#1A1A1A',
    UI_ACCENT: '#1B4F72',
  },
  dusk: {
    NODTA: '#3E4A55',
    LANDA: '#5C5040',
    CSTLN: '#A89074',
    DEPDW: '#1F2A33',
    DEPMS: '#283742',
    DEPVS: '#324553',
    DEPIT: '#4A4030',
    DEPCN: '#6C7984',
    SNDG2: '#9BA8B0',
    SNDG1: '#E0E6EB',
    CHBLK_RED: '#D86C5B',
    CHBLK_GREEN: '#4DAE7A',
    CHBLK_YELLOW: '#D7B05A',
    CHBLK: '#D6DCE0',
    LITYW: '#F5D672',
    CHMGD: '#C77AB2',
    CHGRD: '#B5BCC2',
    UI_BG: '#2A333B',
    UI_FG: '#E6ECEF',
    UI_ACCENT: '#5DADE2',
  },
  night: {
    NODTA: '#0B0F12',
    LANDA: '#1A1610',
    CSTLN: '#6E5B45',
    DEPDW: '#06090C',
    DEPMS: '#0C1318',
    DEPVS: '#121C24',
    DEPIT: '#1F1A12',
    DEPCN: '#3F4A55',
    SNDG2: '#5A6A74',
    SNDG1: '#E84A4A',
    CHBLK_RED: '#E84A4A',
    CHBLK_GREEN: '#3DBE6E',
    CHBLK_YELLOW: '#C9A14F',
    CHBLK: '#E6ECEF',
    LITYW: '#F4D35E',
    CHMGD: '#C77AB2',
    CHGRD: '#9AA1A6',
    UI_BG: '#06090C',
    UI_FG: '#E6ECEF',
    UI_ACCENT: '#E84A4A',
  },
};

export function getPalette(p: Palette): S52Palette {
  return PALETTES[p];
}
